import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

const storageKey = (formKey: string) => `mtli-partial-${formKey}`;

export const getPartialLeadId = (formKey: string): string | null =>
  typeof window === "undefined" ? null : sessionStorage.getItem(storageKey(formKey));

export const clearPartialLeadId = (formKey: string) => {
  if (typeof window !== "undefined") sessionStorage.removeItem(storageKey(formKey));
};

/**
 * Retargeting capture: once a visitor has typed enough to be reachable
 * (valid-looking email or a 7+ digit phone), the partly-filled form saves
 * server-side as an incomplete lead. Debounced, upserts a single row via
 * save-lead-progress using a sessionStorage lead id, and the final submit
 * completes the same row so no duplicates are created.
 */
export function usePartialLeadSave(
  data: Record<string, unknown>,
  formKey: string,
  done: boolean,
) {
  const timer = useRef<number | undefined>(undefined);
  const serialized = JSON.stringify(data);
  useEffect(() => {
    if (done) return;
    const email = String((data as Record<string, unknown>).email ?? "");
    const phone = String((data as Record<string, unknown>).phone ?? "").replace(/\D/g, "");
    const reachable = /.+@.+\..+/.test(email) || phone.length >= 7;
    if (!reachable) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      try {
        const res = await supabase.functions.invoke("save-lead-progress", {
          body: {
            lead_id: getPartialLeadId(formKey),
            step: 0,
            is_complete: false,
            data: { ...data, source_page: window.location.pathname },
          },
        });
        const id = (res.data as { lead_id?: string } | null)?.lead_id;
        if (id) sessionStorage.setItem(storageKey(formKey), id);
      } catch {
        // best effort only
      }
    }, 2500);
    return () => window.clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, done, formKey]);
}
